import React, { useState, useEffect } from "react";
import { AppContext } from "../../../../AppContext";
import { X, FileText, Upload, FileStack, Search } from "lucide-react";

const ManageUploadedPopup = ({ isUploadDocument, uploadDocumentPages, deletedDocumentPages, totalDocumentSize, setIsUploadDocument, selectedLanguage,
  setAlertMsg, setAlertType, documentId, documentName, setOpen, onSuccess, isPageLevelRestrictionChecked, pageRemovalWarnModalRef,
  hasGridGuardOptions, onExcelStatusChange }) => {
  const [pages, setPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [deletedPages, setDeletedPages] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [hasChanges, setHasChanges] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewPageNumber, setPreviewPageNumber] = useState(null);
  const [selectedFileFilter, setSelectedFileFilter] = useState(null);
  const [pageSearchQuery, setPageSearchQuery] = useState("");
  const [showGridGuardRemoveConfirm, setShowGridGuardRemoveConfirm] = useState(false);
  const uploadFileRef = React.useRef(null);
  const initialDeletedIds = React.useRef(new Set());
  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  const handlePreviewPage = async (page) => {
    setOpen(true);
    if (previewImage) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewImage(null);
    const displayNum = activeTab === "bin" ? (page.originalPageNumber ?? page.filePageNumber ?? page.pageNumber) : page.pageNumber;
    setPreviewPageNumber(displayNum);
    setIsPreviewOpen(true);
    try {
      let url = "";
      const pFileName = page.previewFileName || page.fileName || "";
      if (pFileName.includes("Deleted_Page_"))
        url = `${process.env.REACT_APP_API}${process.env.REACT_APP_API_RPDPreview}/${documentId}?fileName=${encodeURIComponent(pFileName)}`;
      else
        url = `${process.env.REACT_APP_API}${process.env.REACT_APP_API_RPDPreview}/${documentId}?fileName=${encodeURIComponent(pFileName)}&filePageNumber=${page.filePageNumber}&isUploaded=${page.isUploaded}`;
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${localStorage.getItem("JWtToken")}` }
      });
      if (!response.ok) {
        throw new Error("Failed to load preview.");
      }
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => { setPreviewImage(reader.result); };
      reader.readAsDataURL(blob);
    } catch (ex) {
      console.error(ex);
      setAlertMsg("Failed to load preview image.");
      setAlertType("warning");
      setIsPreviewOpen(false);
    } finally {
      setOpen(false);
    }
  };
  const formatFileName = (name, maxLength = 30) => {
    if (!name) return "";
    return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
  };


  const getFileIndex = React.useCallback((name) => {
    if (!name) return 9999;
    const fileOrder = [];
    const extractName = (p) => {
      if (!p) return null;
      let fn = p.originalFileName || p.fileName || p.name;
      if (fn && fn.startsWith("Deleted_Page_")) return null;
      return fn || null;
    };
    (uploadDocumentPages || []).forEach(p => {
      const fn = extractName(p);
      if (fn && !fileOrder.includes(fn)) fileOrder.push(fn);
    });
    (uploadedFiles || []).forEach(f => {
      const fn = extractName(f);
      if (fn && !fileOrder.includes(fn)) fileOrder.push(fn);
    });
    const cleanName = name.startsWith("Deleted_Page_") ? null : name;
    const idx = fileOrder.indexOf(cleanName || name);
    return idx >= 0 ? idx : 9999;
  }, [uploadDocumentPages, uploadedFiles]);

  const sortPagesByFileAndSequence = React.useCallback((pagesList, initialDocPages = [], sessionUploaded = []) => {
    if (!pagesList || pagesList.length === 0) return [];

    const getNum = (p) => {
      if (!p) return 0;
      if (p.originalPageNumber !== undefined && p.originalPageNumber !== null) return Number(p.originalPageNumber);
      if (p.pageNumber !== undefined && p.pageNumber !== null) return Number(p.pageNumber);
      if (p.filePageNumber !== undefined && p.filePageNumber !== null) return Number(p.filePageNumber);
      return 0;
    };

    return [...pagesList].sort((a, b) => {
      const fileIdxA = getFileIndex(a.fileName || a.originalFileName);
      const fileIdxB = getFileIndex(b.fileName || b.originalFileName);
      if (fileIdxA !== fileIdxB) {
        return fileIdxA - fileIdxB;
      }
      return getNum(a) - getNum(b);
    });
  }, [getFileIndex]);

  const resolveDeletedPage = React.useCallback((page, index, activePagesList) => {
    let origPageNum = page.originalPageNumber ?? page.removedPageNumber;
    let resolvedFileName = page.originalFileName || page.fileName;
    let previewFile = page.fileName;
    let deletedFile = page.fileName;

    const rawName = page.fileName;
    if (rawName && rawName.startsWith("Deleted_Page_")) {
      previewFile = rawName;
      deletedFile = rawName;
      const parts = rawName.split("_");
      if (parts.length >= 3) {
        const parsed = parseInt(parts[2], 10);
        if (!isNaN(parsed) && parsed > 0) {
          origPageNum = parsed;
        }
      }
    }

    let validOriginalName = null;
    if (page.originalFileName && !page.originalFileName.startsWith("Deleted_Page_")) {
      validOriginalName = page.originalFileName;
    } else if (page.fileName && !page.fileName.startsWith("Deleted_Page_")) {
      validOriginalName = page.fileName;
    }

    if (validOriginalName) {
      resolvedFileName = validOriginalName;
    } else {
      const sourcePages = uploadDocumentPages;
      if (sourcePages && sourcePages.length > 0) {
        const exact = sourcePages.find(p => (p.originalPageNumber ?? p.filePageNumber ?? p.pageNumber) === origPageNum);
        if (exact && exact.fileName && !exact.fileName.startsWith("Deleted_Page_")) {
          resolvedFileName = exact.fileName;
        } else {
          const preceding = [...sourcePages].filter(p => (p.originalPageNumber ?? p.filePageNumber ?? p.pageNumber) <= (origPageNum ?? 1) && p.fileName && !p.fileName.startsWith("Deleted_Page_")).sort((a, b) => (b.originalPageNumber ?? b.filePageNumber ?? b.pageNumber) - (a.originalPageNumber ?? a.filePageNumber ?? a.pageNumber))[0];
          if (preceding && preceding.fileName) {
            resolvedFileName = preceding.fileName;
          } else {
            const anyValid = sourcePages.find(p => p.fileName && !p.fileName.startsWith("Deleted_Page_"));
            if (anyValid && anyValid.fileName) {
              resolvedFileName = anyValid.fileName;
            }
          }
        }
      }
      if (!resolvedFileName || resolvedFileName.startsWith("Deleted_Page_")) {
        resolvedFileName = documentName;
      }
    }

    const finalPageNum = origPageNum ?? (index + 1);
    const relFilePageNum = page.filePageNumber ?? finalPageNum;

    return {
      ...page,
      id: page.id || crypto.randomUUID(),
      pageNumber: finalPageNum,
      originalPageNumber: finalPageNum,
      filePageNumber: relFilePageNum,
      fileName: resolvedFileName,
      originalFileName: resolvedFileName,
      previewFileName: previewFile,
      deletedFileName: deletedFile,
      isUploaded: false
    };
  }, [documentName, uploadDocumentPages]);

  useEffect(() => {
    if (!isUploadDocument) return;
    const tempPages = (uploadDocumentPages || []).map((page, index) => ({
      ...page,
      id: crypto.randomUUID(),
      originalPageNumber: page.originalPageNumber ?? page.filePageNumber ?? page.pageNumber ?? (index + 1),
      pageNumber: index + 1,
      isUploaded: false
    }));
    const rawDeleted = (deletedDocumentPages || []).map((page, index) => resolveDeletedPage(page, index, uploadDocumentPages));
    const seen = new Set();
    const tempDeletedPages = [];
    for (const dp of rawDeleted) {
      const dedupKey = dp.deletedFileName || `${dp.fileName}-${dp.originalPageNumber ?? dp.pageNumber}`;
      if (!seen.has(dedupKey)) {
        seen.add(dedupKey);
        tempDeletedPages.push(dp);
      }
    }
    setPages(tempPages);
    setDeletedPages(tempDeletedPages);
    initialDeletedIds.current = new Set(tempDeletedPages.map(page => page.id));
    setSelectedPages([]);
    setUploadedFiles([]);
    setHasChanges(false);
    setSelectedFileFilter(null);
    setPageSearchQuery("");
    setShowGridGuardRemoveConfirm(false);
    if (uploadFileRef.current)
      uploadFileRef.current.value = "";
  }, [isUploadDocument, uploadDocumentPages, deletedDocumentPages, resolveDeletedPage]);

  const getNewlyRemovedPageNumbers = () => deletedPages.filter(page => !initialDeletedIds.current.has(page.id)).map(page => page.originalPageNumber ?? page.filePageNumber).filter(pageNumber => !!pageNumber);
  const shouldShowPageRemovalWarning = (newlyRemovedPageNumbers) => {
    return isPageLevelRestrictionChecked && newlyRemovedPageNumbers.length > 0;
  };

  const handleCheckbox = (id) => {
    if (activeTab === "bin") return;
    setSelectedPages(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const isExcelPage = (page) => /\.(xls|xlsx)$/i.test(page?.fileName || "") || /\.(xls|xlsx)$/i.test(page?.originalFileName || "");

  const handleRemoveSelected = () => {
    const remainingPages = pages.filter(page => !selectedPages.includes(page.id)).map((page, index) => ({ ...page, pageNumber: index + 1 }));
    if (remainingPages?.length === 0) {
      setAlertMsg(selectedLanguage?.LstLanguageCodes?.UI_AtLeastOnePageMustRemain);
      setAlertType("warning");
      return;
    }
    // Removing the last Excel file while GridGuard options are configured needs user confirmation
    const isRemovingLastExcel = hasGridGuardOptions === true && pages.some(isExcelPage) && !remainingPages.some(isExcelPage);
    if (isRemovingLastExcel) {
      setShowGridGuardRemoveConfirm(true);
      return;
    }
    removeSelectedPages();
  };

  const handleGridGuardRemoveCancel = () => setShowGridGuardRemoveConfirm(false);

  const handleGridGuardRemoveConfirm = () => {
    setShowGridGuardRemoveConfirm(false);
    removeSelectedPages();
  };

  const removeSelectedPages = () => {
    const remainingPages = pages.filter(page => !selectedPages.includes(page.id)).map((page, index) => ({ ...page, pageNumber: index + 1 }));
    const newlyDeleted = pages.filter(page => selectedPages.includes(page.id)).map(page => ({
      id: crypto.randomUUID(),
      pageNumber: page.originalPageNumber ?? page.filePageNumber ?? page.pageNumber,
      fileName: page.fileName,
      originalFileName: page.originalFileName || page.fileName,
      filePageNumber: page.filePageNumber,
      previewFileName: page.previewFileName || page.fileName,
      deletedFileName: page.deletedFileName || (page.fileName?.startsWith("Deleted_Page_") ? page.fileName : null),
      originalPageNumber: page.originalPageNumber ?? page.filePageNumber ?? page.pageNumber,
      isUploaded: page.isUploaded
    }));
    setDeletedPages(prev => [...prev, ...newlyDeleted]);
    setPages(remainingPages);
    if (selectedFileFilter && !remainingPages.some(page => page.fileName === selectedFileFilter)) {
      setSelectedFileFilter(null);
    }
    setSelectedPages([]);
    setHasChanges(true);
  };

  const getPageSequence = (filePages) => {
    if (!filePages || filePages.length === 0) return "";
    const numbers = filePages.map(p => (activeTab === "bin" ? (p.originalPageNumber ?? p.filePageNumber ?? p.pageNumber) : p.pageNumber)).filter(n => n !== undefined && n !== null).sort((a, b) => a - b);
    if (numbers.length === 0) return "";
    const ranges = [];
    let start = numbers[0];
    let end = numbers[0];
    for (let i = 1; i < numbers.length; i++) {
      if (numbers[i] === end + 1) {
        end = numbers[i];
      } else {
        ranges.push(start === end ? `${start}` : `${start}-${end}`);
        start = numbers[i];
        end = numbers[i];
      }
    }
    ranges.push(start === end ? `${start}` : `${start}-${end}`);
    return `(Pages ${ranges.join(', ')})`;
  };

  const handleRestoreSelected = () => {
    const restoringPages = deletedPages.filter(page => selectedPages.includes(page.id));
    if (restoringPages.length === 0) return;
    const formattedRestoring = restoringPages.map(page => ({
      ...page,
      id: crypto.randomUUID(),
      fileName: page.originalFileName && !page.originalFileName.startsWith("Deleted_Page_") ? page.originalFileName : page.fileName,
      filePageNumber: page.filePageNumber,
      originalPageNumber: page.originalPageNumber,
      previewFileName: page.deletedFileName || page.previewFileName || page.fileName,
      deletedFileName: page.deletedFileName
    }));
    let updatedPages = [...pages, ...formattedRestoring];
    updatedPages = sortPagesByFileAndSequence(updatedPages, uploadDocumentPages, uploadedFiles);
    updatedPages = updatedPages.map((page, index) => ({ ...page, pageNumber: index + 1 }));
    const remainingDeleted = deletedPages.filter(page => !selectedPages.includes(page.id));
    setPages(updatedPages);
    setDeletedPages(remainingDeleted);
    if (selectedFileFilter && !remainingDeleted.some(page => page.fileName === selectedFileFilter)) {
      setSelectedFileFilter(null);
    }
    setSelectedPages([]);
    setHasChanges(true);
  };

  const handleUploadSelection = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    let xlsFilesSelected = false;
    let validationSuccess = true;
    const currentUser = localStorage.getItem("UserName")?.toLowerCase();
    const readersList = AppContext.ExcelConfigReaders?.[0]?.split(",").map(email => email.trim().toLowerCase()).filter(Boolean) || [];
    const isConfiguredUser = readersList.includes(currentUser);
    for (let index = 0; index < selectedFiles.length; index++) {
      const objFile = selectedFiles[index];
      const fileNameWithoutExtension = objFile.name.substring(0, objFile.name.lastIndexOf("."));
      const fileFormat = objFile.name.split(".").pop().toLowerCase();
      // Excel v  alidation
      if (fileFormat === "xls" || fileFormat === "xlsx") {
        if (isConfiguredUser) {
          xlsFilesSelected = true;
        }
        else {
          setAlertMsg(selectedLanguage.LstLanguageCodes.UI_UnsupportedFileExtensions + " " + selectedLanguage.LstLanguageCodes.UI_CurrentlySupportedFileExtensions);
          setAlertType("warning");
          validationSuccess = false;
          xlsFilesSelected = false;
        }
      }
      // Unsupported extension
      if ("pdf/pptx/docx/jpg/jpeg/png/wps/dwg/xls/xlsx".indexOf(fileFormat) === -1) {
        event.target.value = "";
        setAlertMsg(selectedLanguage.LstLanguageCodes.UI_UnsupportedFileExtensions + " " + (isConfiguredUser ? selectedLanguage.LstLanguageCodes.UI_XLSCurrentlySupportedFileExtensions : selectedLanguage.LstLanguageCodes.UI_CurrentlySupportedFileExtensions));
        setAlertType("warning");
        validationSuccess = false;
      }
      // CSV validation
      if (fileFormat === "csv" && isConfiguredUser) {
        setAlertMsg(selectedLanguage.LstLanguageCodes.UI_UnsupportedFileExtensions + " " + selectedLanguage.LstLanguageCodes.UI_XLSCurrentlySupportedFileExtensions);
        setAlertType("warning");
        validationSuccess = false;
        xlsFilesSelected = false;
      }
      // File size validation
      if (objFile.size > AppContext.FileSizeCreate * 1024 * 1024) {
        event.target.value = "";
        setAlertMsg(selectedLanguage.LstLanguageCodes.UI_Themaximumsizelessthan25);
        setAlertType("warning");
        validationSuccess = false;
      }
      // File name validation
      if (fileNameWithoutExtension.length > AppContext.FileNameLength) {
        event.target.value = "";
        setAlertMsg(selectedLanguage.LstLanguageCodes.UI_FileNameShouldNotbegreaterthan200);
        setAlertType("warning");
        validationSuccess = false;
      }
    }
    if (!validationSuccess) return;

    const newlySelectedSize = selectedFiles.reduce((acc, f) => acc + (f.size || 0), 0);
    const sessionUploadedSize = uploadedFiles.reduce((acc, f) => acc + (f.size || 0), 0);
    const existingTotalSize = (totalDocumentSize || 0) + sessionUploadedSize;
    if (existingTotalSize + newlySelectedSize > AppContext.FileSizeCreate * 1024 * 1024) {
      event.target.value = "";
      setAlertMsg(selectedLanguage.LstLanguageCodes.UI_Themaximumsizelessthan25);
      setAlertType("warning");
      return;
    }

    await uploadDocumentPagesAPI(selectedFiles);
    event.target.value = "";
  };

  const uploadDocumentPagesAPI = async (selectedFiles) => {
    const isValid = await validateDuplicateFiles(selectedFiles);
    if (isValid) {
      setOpen(true);
      const sessionUploadedSize = uploadedFiles.reduce((acc, f) => acc + (f.size || 0), 0);
      const existingTotalSize = (totalDocumentSize || 0) + sessionUploadedSize;
      const formData = new FormData();
      formData.append("UserName", localStorage.getItem("UserName"));
      formData.append("MultpleRdocs", "false");
      formData.append("documentID", documentId);
      formData.append("documentOptions", "");
      formData.append("inputfiles", "[]");
      formData.append("existingPagesCount", pages.length);
      formData.append("existingFileSize", existingTotalSize);
      selectedFiles.forEach(file => { formData.append("files", file); });
      try {
        const response = await fetch(`${process.env.REACT_APP_API}${process.env.REACT_APP_API_GetPageNumbers}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("JWtToken")}` },
          body: formData
        });
        const result = await response.json();
        if (!response.ok) {
          setAlertMsg(result.errorMessage || "Upload failed.");
          setAlertType("warning");
          setOpen(false);
          return;
        }
        appendUploadedPages(result, selectedFiles);
      }
      catch (ex) {
        setAlertMsg(ex.message);
        setAlertType("warning");
      }
      setOpen(false);
    }
  };

  const appendUploadedPages = (result, selectedFiles) => {
    setPages(prev => {
      let nextPageNumber = prev.length + 1;
      const uploadedPages = result.pages.map(page => ({
        id: crypto.randomUUID(),
        pageNumber: nextPageNumber++,
        originalPageNumber: null,
        fileName: page.fileName,
        originalFileName: page.fileName,
        filePageNumber: page.filePageNumber,
        previewFileName: page.fileName,
        isUploaded: true
      }));
      const combined = [...prev, ...uploadedPages];
      return sortPagesByFileAndSequence(combined, uploadDocumentPages, [...uploadedFiles, ...selectedFiles]);
    });
    setUploadedFiles(prev => [...prev, ...selectedFiles]);
    setHasChanges(true);
  };

  const validateDuplicateFiles = (selectedFiles) => {
    const existingFiles = new Set(pages.map(page => page.fileName?.toLowerCase()).filter(Boolean));
    const existingFilesInBin = new Set(deletedDocumentPages.map(page => page.originalFileName?.toLowerCase()).filter(Boolean));
    const isFilesInBin = new Set(deletedPages.map(page => page.fileName?.toLowerCase()).filter(Boolean));
    for (const file of selectedFiles) {
      if (existingFiles.has(file.name.toLowerCase()) || existingFilesInBin.has(file.name.toLowerCase()) || isFilesInBin.has(file.name.toLowerCase())) {
        setAlertMsg(selectedLanguage.LstLanguageCodes.UI_DuplicateFilesAreNotallowed);
        setAlertType("warning");
        return false;
      }
    }
    return true;
  };

  const handleConfirm = async () => {
    if (!hasChanges) {
      setIsUploadDocument(false);
      return;
    }
    const newlyRemovedPageNumbers = getNewlyRemovedPageNumbers();
    if (shouldShowPageRemovalWarning(newlyRemovedPageNumbers) && pageRemovalWarnModalRef?.current) {
      pageRemovalWarnModalRef.current.open(selectedLanguage.LstLanguageCodes.UI_PageRemovalWarning, async () => { await SaveRPDFile(); }, () => { (handleCancel(true)) });
      return;
    }

    await SaveRPDFile();
  };

  const SaveRPDFile = async () => {
    setOpen(true);
    setActiveTab("active");
    const formData = new FormData();
    formData.append("DocumentId", documentId);
    uploadedFiles.forEach(file => { formData.append("Files", file); });

    const payloadPages = pages.map(page => ({
      pageNumber: page.pageNumber,
      fileName: page.deletedFileName && page.deletedFileName.includes("Deleted_Page_") ? page.deletedFileName : page.fileName,
      filePageNumber: page.filePageNumber,
      originalPageNumber: page.originalPageNumber,
      isUploaded: Boolean(page.isUploaded)
    }));

    const payloadDeletedPages = deletedPages.map(page => ({
      pageNumber: page.originalPageNumber ?? page.filePageNumber ?? page.pageNumber,
      fileName: page.deletedFileName && page.deletedFileName.includes("Deleted_Page_") ? page.deletedFileName : page.fileName,
      filePageNumber: page.filePageNumber,
      originalPageNumber: page.originalPageNumber ?? page.filePageNumber ?? page.pageNumber,
      isUploaded: Boolean(page.isUploaded)
    }));

    formData.append("Pages", JSON.stringify(payloadPages));
    formData.append("DeletedPages", JSON.stringify(payloadDeletedPages));
    const formatBytesToSize = (bytes) => {
      const kb = bytes / 1024;
      if (kb >= 1024) {
        return `${Math.round(kb / 1024)} MB`;
      }
      return `${Math.round(kb)} KB`;
    };
    const newInputFiles = uploadedFiles.map((file, index) => ({
      FileName: file.name,
      ModifiedFileName: file.name,
      FileSize: formatBytesToSize(file.size),
      Order: index,
      Hash: null
    }));
    formData.append("InputFiles", JSON.stringify(newInputFiles));
    try {
      const response = await fetch(`${process.env.REACT_APP_API}${process.env.REACT_APP_API_SaveRPDFile}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("JWtToken")}` },
        body: formData
      });
      const result = await response.json();
      if (!response.ok) {
        setAlertMsg(result.errorMessage);
        setAlertType("warning");
        return;
      }
      if (onSuccess) {
        await onSuccess();
      }
      if (onExcelStatusChange) {
        onExcelStatusChange({ hasActiveExcel: pages.some(isExcelPage), wasGridGuardConfigured: hasGridGuardOptions === true });
      }
      setIsUploadDocument(false);
    }
    finally {
      setOpen(false);
    }
  };

  const handleCancel = (isFromPageLocker = false) => {
    setUploadedFiles([]);
    setDeletedPages([]);
    setSelectedPages([]);
    setHasChanges(false);
    setActiveTab("active");
    setSelectedFileFilter(null);
    setPageSearchQuery("");
    setShowGridGuardRemoveConfirm(false);
    const tempPages = (uploadDocumentPages || []).map((page, index) => ({
      ...page,
      id: crypto.randomUUID(),
      originalPageNumber: page.originalPageNumber ?? page.filePageNumber ?? page.pageNumber ?? (index + 1),
      pageNumber: index + 1,
      isUploaded: false
    }));
    const rawDeleted = (deletedDocumentPages || []).map((page, index) => resolveDeletedPage(page, index, uploadDocumentPages));
    const seen = new Set();
    const tempDeletedPages = [];
    for (const dp of rawDeleted) {
      const dedupKey = dp.deletedFileName || `${dp.fileName}-${dp.originalPageNumber ?? dp.pageNumber}`;
      if (!seen.has(dedupKey)) {
        seen.add(dedupKey);
        tempDeletedPages.push(dp);
      }
    }
    setPages(tempPages);
    setDeletedPages(tempDeletedPages);
    if (uploadFileRef.current) {
      uploadFileRef.current.value = "";
    }
    if (!isFromPageLocker) {
      setIsUploadDocument(false);
    }
  };

  useEffect(() => {
    const currentList = activeTab === "active" ? pages : deletedPages;
    if (selectedFileFilter && !currentList.some(page => page.fileName === selectedFileFilter)) {
      setSelectedFileFilter(null);
    }
  }, [activeTab, pages, deletedPages, selectedFileFilter]);

  if (!isUploadDocument) return null;
  const searchFilter = p => {
    if (!pageSearchQuery || !pageSearchQuery.trim()) return true;
    const query = pageSearchQuery.toLowerCase().trim();
    const displayNumStr = (activeTab === "bin" ? (p.originalPageNumber ?? p.filePageNumber ?? p.pageNumber) : p.pageNumber)?.toString().toLowerCase() || "";
    const fileNameStr = p.fileName?.toLowerCase() || "";
    return displayNumStr.includes(query) || fileNameStr.includes(query);
  };
  const currentTabPages = activeTab === "active" ? pages : deletedPages;
  const allGroupedPages = currentTabPages.reduce((acc, page) => {
    const name = page.fileName;
    if (!acc[name]) acc[name] = [];
    acc[name].push(page);
    return acc;
  }, {});
  const visiblePages = currentTabPages.filter(searchFilter);
  const groupedPages = visiblePages.reduce((acc, page) => {
    const name = page.fileName;
    if (!acc[name]) acc[name] = [];
    acc[name].push(page);
    return acc;
  }, {});
  // Pages in the active view
  const getPagesInView = () => {
    if (selectedFileFilter === null) return visiblePages;
    return groupedPages[selectedFileFilter] || [];
  };
  const currentViewPages = getPagesInView();
  const totalViewCount = selectedFileFilter === null ? currentTabPages.length : (allGroupedPages[selectedFileFilter]?.length || 0);
  // "Select All" checkbox state
  const isAllSelected = currentViewPages.length > 0 && currentViewPages.every(p => selectedPages.includes(p.id));
  const handleSelectAllToggle = e => {
    if (activeTab === "bin") return;
    const shouldSelect = e.target.checked;
    if (shouldSelect) {
      const idsToAdd = currentViewPages.map(p => p.id);
      setSelectedPages(prev => Array.from(new Set([...prev, ...idsToAdd])));
    } else {
      const idsToRemove = new Set(currentViewPages.map(p => p.id));
      setSelectedPages(prev => prev.filter(id => !idsToRemove.has(id)));
    }
  };

  return (
    <div className="child-popup" id="manageUploadPopup">
      <style>{`.sidebar-file-btn:hover {background-color: #f1f5f9 !important;}
        .upload-files svg {
          transition: transform 0.2s ease-in-out, filter 0.2s ease-in-out;
        }
        .upload-files svg:hover { transform: scale(1.08); filter: drop-shadow(0 4px 6px rgba(63, 162, 255, 0.4)); }
        .upload-files svg:hover text {
          fill: #007bff !important;
        }
        .upload-files svg:hover line {
          stroke: #007bff !important;
        }
        .page-search-input:focus {
          border-color: #3b82f6 !important;
          background-color: #ffffff !important;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }
      `}</style>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "82rem", width: "95vw", height: "88vh", minHeight: "88vh", maxHeight: "90vh", margin: "auto" }}>
        <div className="modal-content" style={{ height: "100%", minHeight: "100%", borderRadius: "16px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {/* Modal Header */}
          <div className="modal-header" style={{ padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0" }}>
            <h5 className="d-flex align-items-center mb-0" style={{ fontWeight: 600, fontSize: "18px" }}>
              {selectedLanguage?.LstLanguageCodes?.UI_ManageRPD}
            </h5>
            <div className="card border-0 mb-0">
              <ul className="nav nav-tabs common-tabs" style={{ borderBottom: "none", backgroundColor: "#ffffff" }}>
                <li className="nav-item">
                  <a
                    href="#column"
                    className={`nav-link manage-link boxshadow active-pages ${activeTab === "active" ? "active" : ""}`}
                    onClick={(e) => { e.preventDefault(); setActiveTab("active"); setSelectedPages([]); setSelectedFileFilter(null); }}
                  >
                    {selectedLanguage?.LstLanguageCodes?.UI_ActivePages} ({pages.length})
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    href="#filter"
                    style={{ boxShadow: "none !important", borderBottom: activeTab === "bin" ? "#bc272e" : "2px solid white", marginBottom: "0px" }}
                    className={`boxshadow nav-link manage-link ${activeTab === "bin" ? "active" : ""}`}
                    onClick={(e) => { e.preventDefault(); setActiveTab("bin"); setSelectedPages([]); setSelectedFileFilter(null); }}
                  >
                    {selectedLanguage?.LstLanguageCodes?.UI_Bin} ({deletedPages.length})
                  </a>
                </li>
              </ul>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "187px" }}>
              <button className="btn btn-danger" disabled={activeTab !== "active" || selectedPages.length === 0}
                onClick={handleRemoveSelected} style={{ visibility: activeTab === "active" ? "visible" : "hidden" }} tabIndex={activeTab === "active" ? 0 : -1}>
                {selectedLanguage.LstLanguageCodes.UI_RemoveSelected}
              </button>
              <button type="button" className="btn btn-outline-danger modalclose download" onClick={() => handleCancel(false)}>
                <span className="material-icons font-20"> highlight_off </span>
              </button>
            </div>
          </div>

          {/* Modal Body: Split-Pane */}
          <div className="modal-body p-0" style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
            {/* Left Sidebar: Document Structure */}
            <div className="document-structure-sidebar"
              style={{ width: "270px", minWidth: "270px", maxWidth: "270px", backgroundColor: "#ffffff", borderRight: "1px solid #e2e8f0", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {/* Sidebar Scrollable Content */}
              <div style={{ flex: 1, overflowY: "auto", padding: "12px" }}>
                {/* All Documents Item */}
                <div onClick={() => setSelectedFileFilter(null)} className="sidebar-file-btn"
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: "8px", cursor: "pointer", backgroundColor: selectedFileFilter === null ? "#eff6ff" : "transparent",
                    border: selectedFileFilter === null ? "1px solid #bfdbfe" : "1px solid transparent", color: selectedFileFilter === null ? "#1d4ed8" : "#334155", fontWeight: selectedFileFilter === null ? 600 : 500, marginBottom: "12px", transition: "all 0.15s ease"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FileStack size={16} color={selectedFileFilter === null ? "#2563eb" : "#64748b"} />
                    <span style={{ fontSize: "13px" }}>{selectedLanguage?.LstLanguageCodes?.UI_RPDDocuments}</span>
                  </div>
                  <span style={{ backgroundColor: selectedFileFilter === null ? "#dbeafe" : "#f1f5f9", color: selectedFileFilter === null ? "#1e40af" : "#64748b", fontSize: "11px", fontWeight: 700, padding: "2px 7px", borderRadius: "10px" }}>
                    {currentTabPages.length}
                  </span>
                </div>

                {/* Files Section Title */}
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", padding: "4px 4px 8px 4px" }}>
                  FILES ({Object.keys(allGroupedPages).length})
                </div>

                {/* File List */}
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {Object.entries(allGroupedPages)
                    .sort(([a], [b]) => getFileIndex(a) - getFileIndex(b))
                    .map(([fileName, filePages]) => {
                      const isSelected = selectedFileFilter === fileName;
                      return (
                        <div key={fileName} onClick={() => {
                          if (selectedFileFilter === null && isAllSelected) {
                            setSelectedPages([]);
                          }
                          setSelectedFileFilter(fileName);
                        }} title={fileName} className="sidebar-file-btn"
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderRadius: "8px", cursor: "pointer", backgroundColor: isSelected ? "#eff6ff" : "transparent",
                            border: isSelected ? "1px solid #bfdbfe" : "1px solid transparent", color: isSelected ? "#1d4ed8" : "#334155", transition: "all 0.15s ease"
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", overflow: "hidden" }}>
                            <FileText size={16} color={isSelected ? "#2563eb" : "#64748b"} style={{ flexShrink: 0, marginTop: "2px" }} />
                            <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                              <span style={{ fontSize: "13px", fontWeight: isSelected ? 600 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: "1.3" }}>
                                {formatFileName(fileName, 22)}
                              </span>
                              <span style={{ fontSize: "11px", color: isSelected ? "#3b82f6" : "#64748b", marginTop: "2px" }}>
                                {getPageSequence(filePages)}
                              </span>
                            </div>
                          </div>
                          <span style={{
                            backgroundColor: isSelected ? "#dbeafe" : "#f1f5f9", color: isSelected ? "#1e40af" : "#64748b",
                            fontSize: "11px", fontWeight: 700, padding: "2px 7px", borderRadius: "10px", flexShrink: 0, marginLeft: "6px"
                          }}>
                            {filePages.length}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>

            {/* Right Main Content Area */}
            <div className="main-pages-area" style={{ flex: 1, display: "flex", flexDirection: "column", backgroundColor: "#ffffff", overflow: "hidden" }}>
              {/* Toolbar */}
              <div style={{
                padding: "12px 20px", backgroundColor: "#ffffff", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: "16px", flexWrap: "wrap"
              }}>
                {/* View Title */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }} title={selectedFileFilter || (selectedLanguage?.LstLanguageCodes?.UI_RPDDocuments)}>
                  <span style={{ fontSize: "15px", fontWeight: 600, color: "#1e293b", maxWidth: "450px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selectedFileFilter === null ? (selectedLanguage?.LstLanguageCodes?.UI_RPDDocuments) : selectedFileFilter}
                  </span>
                  <span style={{ fontSize: "13px", color: "#64748b", fontWeight: 500, flexShrink: 0 }}>
                    ({totalViewCount} {totalViewCount === 1 ? "page" : "pages"})
                  </span>
                </div>

                {/* Search & Select All */}
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  {/* Search Input */}
                  <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                    <Search size={15} color="#94a3b8" style={{ position: "absolute", left: "10px", pointerEvents: "none" }} />
                    <input type="text" placeholder="Search by page number or filename..." value={pageSearchQuery} onChange={e => setPageSearchQuery(e.target.value)}
                      style={{ padding: "6px 12px 6px 32px", fontSize: "13px", border: "1px solid #cbd5e1", borderRadius: "6px", width: "270px", outline: "none", backgroundColor: "#ffffff" }} className="page-search-input"
                    />
                    {pageSearchQuery && (
                      <button type="button" onClick={() => setPageSearchQuery("")}
                        style={{ position: "absolute", right: "8px", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: "0", display: "flex", alignItems: "center" }}>
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* Select All Checkbox (No Clear button) */}
                  <label style={{
                    display: "flex", alignItems: "center", gap: "6px", margin: 0, cursor: activeTab === "bin" ? "not-allowed" : "pointer",
                    fontSize: "13px", fontWeight: 500, color: activeTab === "bin" ? "#94a3b8" : "#334155", userSelect: "none", opacity: activeTab === "bin" ? 0.6 : 1
                  }}>
                    <input type="checkbox" disabled={activeTab === "bin"} checked={activeTab === "bin" ? false : isAllSelected} onChange={handleSelectAllToggle} style={{ cursor: activeTab === "bin" ? "not-allowed" : "pointer", width: "16px", height: "16px" }} />
                    <span>{selectedLanguage?.LstLanguageCodes?.UI_SelectAll}</span>
                  </label>
                </div>
              </div>

              {/* Pages Grid Container */}
              <div key={activeTab} style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
                {currentViewPages.length === 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", minHeight: "220px", color: "#94a3b8", gap: "10px" }}>
                    <FileText size={40} strokeWidth={1.5} color="#cbd5e1" />
                    <span style={{ fontSize: "14px", fontWeight: 500 }}>
                      {activeTab === "bin" ? (selectedLanguage?.LstLanguageCodes?.UI_NoPagesFoundInBin) : ("No Pages found in Active Pages")}
                    </span>
                  </div>
                ) : selectedFileFilter === null ? (
                  /* All Documents: Grouped by File */
                  <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                    {Object.entries(groupedPages)
                      .sort(([a], [b]) => getFileIndex(a) - getFileIndex(b))
                      .map(([fName, filePages]) => {
                        const sortedFilePages = [...filePages].sort((a, b) => {
                          const numA = activeTab === "bin" ? (a.originalPageNumber ?? a.filePageNumber ?? a.pageNumber ?? 0) : a.pageNumber;
                          const numB = activeTab === "bin" ? (b.originalPageNumber ?? b.filePageNumber ?? b.pageNumber ?? 0) : b.pageNumber;
                          return numA - numB;
                        });
                        return (
                          <div key={`${activeTab}-${fName}`} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            {/* Group Header */}
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingBottom: "8px", borderBottom: "1px solid #e2e8f0" }} title={fName}>
                              <FileText size={16} color="#2563eb" style={{ flexShrink: 0 }} />
                              <span style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b", maxWidth: "450px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {fName}
                              </span>
                              <span
                                style={{ backgroundColor: "#f1f5f9", color: "#64748b", fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "10px", marginLeft: "4px", flexShrink: 0 }}>
                                {(allGroupedPages[fName] || filePages).length} {(allGroupedPages[fName] || filePages).length === 1 ? "page" : "pages"}
                              </span>
                            </div>
                            {/* Group Pages */}
                            <div className="preview-pages">
                              {sortedFilePages.map(page => (
                                <div key={page.id} className="upload-files" style={{ width: "70px", textAlign: "center" }}>
                                  <svg width="52" height="66" viewBox="0 0 52 66" style={{ cursor: "pointer" }} onClick={() => handlePreviewPage(page)}>
                                    <path d="M6 2 H33 L46 15 V60 C46 62 44 64 42 64 H6 C4 64 2 62 2 60 V6 C2 4 4 2 6 2 Z" fill="white" stroke="black" strokeWidth="3" />
                                    <path d="M33 2 V15 H46" fill="white" stroke="black" strokeWidth="3" strokeLinejoin="round" />
                                    <text x="26" y="41" textAnchor="middle" fontSize="9" fill="#3fa2ff" fontWeight="600">
                                      {selectedLanguage?.LstLanguageCodes?.UI_Preview}
                                    </text>
                                    <line x1="10" y1="44" x2="42" y2="44" stroke="#3fa2ff" strokeWidth="1" />
                                  </svg>
                                  <div className="mt-2" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                    <input type="checkbox" disabled={activeTab === "bin"} checked={selectedPages.includes(page.id)} onChange={() => handleCheckbox(page.id)}
                                      style={{ cursor: activeTab === "bin" ? "not-allowed" : "pointer" }}
                                    />
                                    <span style={{ marginLeft: "4px" }}>
                                      {activeTab === "bin" ? (page.originalPageNumber ?? page.filePageNumber ?? page.pageNumber) : page.pageNumber}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  /* Single Filtered File Pages */
                  <div className="preview-pages">
                    {[...currentViewPages]
                      .sort((a, b) => {
                        const numA = activeTab === "bin" ? (a.originalPageNumber ?? a.filePageNumber ?? a.pageNumber ?? 0) : a.pageNumber;
                        const numB = activeTab === "bin" ? (b.originalPageNumber ?? b.filePageNumber ?? b.pageNumber ?? 0) : b.pageNumber;
                        return numA - numB;
                      })
                      .map(page => (
                        <div key={page.id} className="upload-files" style={{ width: "70px", textAlign: "center" }}>
                          <svg width="52" height="66" viewBox="0 0 52 66" style={{ cursor: "pointer" }} onClick={() => handlePreviewPage(page)}>
                            <path d="M6 2 H33 L46 15 V60 C46 62 44 64 42 64 H6 C4 64 2 62 2 60 V6 C2 4 4 2 6 2 Z" fill="white" stroke="black" strokeWidth="3" />
                            <path d="M33 2 V15 H46" fill="white" stroke="black" strokeWidth="3" strokeLinejoin="round" />
                            <text x="26" y="41" textAnchor="middle" fontSize="9" fill="#3fa2ff" fontWeight="600">
                              {selectedLanguage?.LstLanguageCodes?.UI_Preview}
                            </text>
                            <line x1="10" y1="44" x2="42" y2="44" stroke="#3fa2ff" strokeWidth="1" />
                          </svg>
                          <div className="mt-2" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <input type="checkbox" disabled={activeTab === "bin"} checked={selectedPages.includes(page.id)} onChange={() => handleCheckbox(page.id)} style={{ cursor: activeTab === "bin" ? "not-allowed" : "pointer" }} />
                            <span style={{ marginLeft: "4px" }}>
                              {activeTab === "bin" ? (page.originalPageNumber ?? page.filePageNumber ?? page.pageNumber) : page.pageNumber}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer" style={{ padding: "12px 20px", borderTop: "1px solid #e2e8f0" }}>
            <input ref={uploadFileRef} type="file" multiple style={{ display: "none" }} onChange={handleUploadSelection} />
            <button disabled={activeTab === "bin"} className="btn btn-outline-success" onClick={() => uploadFileRef.current.click()} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Upload size={16} />
              <span>{selectedLanguage?.LstLanguageCodes?.UI_Upload}</span>
            </button>
            <div className="ms-auto" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <button className="btn btn-danger me-2" onClick={() => handleCancel(false)} style={{ minWidth: "85px" }}>
                {selectedLanguage?.LstLanguageCodes?.UI_Cancel}
              </button>
              <button disabled={activeTab === "bin"} className="btn btn-primary" onClick={handleConfirm} style={{ minWidth: "85px" }}>
                {selectedLanguage?.LstLanguageCodes?.UI_Confirm}
              </button>
            </div>
          </div>
        </div>
      </div>
      {isPreviewOpen && (
        <div className="preview-overlay preview-outline" style={{ zIndex: 1060 }}>
          <div className="preview-content-card" onClick={(e) => e.stopPropagation()}
            style={{ margin: "auto", display: "flex", flexDirection: "column", maxHeight: "90vh", maxWidth: "750px", width: "auto" }}
          >
            <div className="preview-screen">
              <h6 style={{ margin: 0, fontWeight: 600, fontSize: "16px" }}>
                {selectedLanguage?.LstLanguageCodes?.UI_LabelPage} {previewPageNumber} {selectedLanguage?.LstLanguageCodes?.UI_Preview}
              </h6>
              <button type="button" className="btn btn-outline-danger modalclose download" onClick={() => setIsPreviewOpen(false)}>
                <span className="material-icons font-20"> highlight_off </span>
              </button>
            </div>
            <div className="preview-image" style={{ minHeight: "unset", maxHeight: "70vh", overflow: "auto", display: "block" }}>
              {previewImage && (
                <img src={previewImage} alt={`Page ${previewPageNumber} Preview`} style={{ width: "auto", maxWidth: "700px", height: "auto", objectFit: "contain", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", display: "block", margin: "0 auto" }} />
              )}
            </div>
          </div>
        </div>
      )}
      {showGridGuardRemoveConfirm && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", zIndex: 1070, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="modal-content" role="alertdialog" aria-modal="true"
            style={{ maxWidth: "480px", width: "90vw", borderRadius: "12px", border: "1px solid #cbd5e1", overflow: "hidden", backgroundColor: "#fff" }}>
            {/* Header */}
            <div style={{ padding: "12px 16px", fontSize: "18px", fontWeight: 600, color: "#000", borderBottom: "1px solid #adb5bd", textAlign: "left" }}>
              Update
            </div>
            {/* Message */}
            <div style={{ padding: "28px 24px 24px", textAlign: "center", fontSize: "17px", fontWeight: 500, lineHeight: "1.6", color: "#475569" }}>
              {selectedLanguage?.LstLanguageCodes?.UI_GG_RemoveAllOptions}
            </div>
            {/* Buttons */}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "24px", padding: "0 24px 28px" }}>
              <button type="button" className="btn btn-danger" onClick={handleGridGuardRemoveCancel}
                style={{ minWidth: "130px", fontWeight: 600 }}>
                {selectedLanguage?.LstLanguageCodes?.UI_Cancel}
              </button>
              <button type="button" className="btn btn-primary" onClick={handleGridGuardRemoveConfirm}
                style={{ minWidth: "130px", fontWeight: 600 }}>
                {selectedLanguage?.LstLanguageCodes?.UI_Confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default ManageUploadedPopup;